/**
 * The application's one top bar.
 *
 * It carries the things that are true everywhere: where you are, what you are
 * searching, which company and financial year you are working in, and who you
 * are signed in as. The company and year live here rather than inside any
 * module because they scope every request the application makes.
 *
 * The order left to right is the order of importance, and each thing is given
 * only the room it needs: the trail of where you are takes the space that is
 * left over and gives it up first, the search is a field where there is room
 * for one and an icon where there is not, and the company and year sit
 * together as one control because they are one idea — the books you are
 * working in.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, CalendarRange, ChevronDown, LogOut, Menu, Search, UserCircle2 } from 'lucide-react';
import { findNavItem } from '@rsc/shared';
import { TASKS } from '@/modules/do/tasks';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from './NotificationBell';

interface TopBarProps {
  onOpenSidebar: () => void;
}

export function TopBar({ onOpenSidebar }: TopBarProps) {
  const { pathname } = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  const match = findNavItem(pathname);
  // A guided task is not in the module tree and has a better name than any
  // module could give it.
  const task = TASKS.find((t) => t.to.split('?')[0] === pathname);
  const home = pathname === '/';
  const title = home ? 'Home' : (task?.label ?? match?.item.label ?? 'RSC ERP');
  const section = task || home ? undefined : match?.section.label;
  const parent = task || home ? undefined : match?.parent?.label;

  /*
   * Installed on a phone there is no browser chrome above this, so on a
   * handset with a notch or a camera cut-out the status bar sits on top of
   * the header and the search box ends up half behind the clock. The inset
   * is zero everywhere else, so this costs nothing on a desktop.
   */
  return (
    <header
      className="flex h-14 shrink-0 items-center gap-2 border-b border-border bg-card px-3 sm:gap-3 sm:px-5"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        height: 'calc(3.5rem + env(safe-area-inset-top, 0px))',
      }}
    >
      <button
        type="button"
        onClick={onOpenSidebar}
        aria-label="Open navigation"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground lg:hidden"
      >
        <Menu className="h-[18px] w-[18px]" />
      </button>

      {/* Where you are: section › group › page. The page's own title carries
          the weight below, so this stays quiet and truncates first. */}
      <div className="min-w-0 flex-1 overflow-hidden">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 whitespace-nowrap text-[14px] text-muted-foreground">
          {section && <span className="hidden shrink truncate lg:inline">{section}</span>}
          {section && <span className="hidden shrink-0 lg:inline" aria-hidden>›</span>}
          {parent && <span className="hidden shrink truncate xl:inline">{parent}</span>}
          {parent && <span className="hidden shrink-0 xl:inline" aria-hidden>›</span>}
          <span className="truncate font-medium text-foreground">{title}</span>
        </nav>
      </div>

      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        aria-label="Search everything"
        title="Search everything (Ctrl K)"
        className="flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-md border border-input bg-background text-muted-foreground transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:w-60 lg:justify-start lg:px-3 xl:w-72"
      >
        <Search className="h-[18px] w-[18px] shrink-0" />
        <span className="hidden flex-1 truncate whitespace-nowrap text-left text-[15px] lg:inline">Search</span>
        <kbd className="hidden shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[12px] lg:inline">
          Ctrl K
        </kbd>
      </button>

      <ScopeSwitcher />
      <NotificationBell />
      <UserMenu />

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}

/**
 * Company and financial year.
 *
 * Changing either reloads the session, because permissions are granted per
 * company and every figure in the application is scoped to the year.
 */
function ScopeSwitcher() {
  const company = useSession((s) => s.company);
  const companies = useSession((s) => s.companies);
  const financialYear = useSession((s) => s.financialYear);
  const financialYears = useSession((s) => s.financialYears);
  const switchScope = useSession((s) => s.switchScope);

  const [open, setOpen] = useState<'company' | 'fy' | null>(null);
  const [busy, setBusy] = useState(false);

  const choose = async (companyId: number, fyId: number | null) => {
    setBusy(true);
    try {
      await switchScope(companyId, fyId);
      setOpen(null);
    } finally {
      setBusy(false);
    }
  };

  if (!company) return null;

  return (
    <div className="hidden shrink-0 items-center rounded-md border border-border bg-background md:flex">
      <div className="relative">
        <button
          type="button"
          disabled={busy}
          onClick={() => setOpen(open === 'company' ? null : 'company')}
          title={company.name}
          className="flex h-10 max-w-[10rem] items-center gap-2 rounded-l-md px-2.5 text-[15px] font-medium transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring xl:max-w-[14rem]"
        >
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{company.name}</span>
          {companies.length > 1 && <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
        </button>

        {open === 'company' && companies.length > 0 && (
          <Dropdown onClose={() => setOpen(null)} label="Company">
            {companies.map((c) => (
              <DropdownItem
                key={c.id}
                selected={c.id === company.id}
                onSelect={() => choose(c.id, null)}
              >
                <span className="truncate">{c.name}</span>
                <span className="ml-2 shrink-0 text-[13px] text-muted-foreground">{c.code}</span>
              </DropdownItem>
            ))}
          </Dropdown>
        )}
      </div>

      {financialYear && (
        <div className="relative border-l border-border">
          <button
            type="button"
            disabled={busy}
            onClick={() => setOpen(open === 'fy' ? null : 'fy')}
            title={`Financial year ${financialYear.name}`}
            className="flex h-10 items-center gap-2 rounded-r-md px-2.5 text-[15px] font-medium transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="whitespace-nowrap">{financialYear.name}</span>
            {financialYears.length > 1 && <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          </button>

          {open === 'fy' && (
            <Dropdown onClose={() => setOpen(null)} label="Financial year">
              {financialYears.map((fy) => (
                <DropdownItem
                  key={fy.id}
                  selected={fy.id === financialYear.id}
                  onSelect={() => choose(company.id, fy.id)}
                >
                  <span>{fy.name}</span>
                  {fy.is_closed ? (
                    <span className="ml-2 shrink-0 text-[13px] text-muted-foreground">closed</span>
                  ) : null}
                </DropdownItem>
              ))}
            </Dropdown>
          )}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const initials = (user.full_name || user.username)
    .split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Account"
        className="flex h-9 items-center gap-2 rounded-md pl-1 pr-1.5 transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary text-[13px] font-semibold text-primary-foreground">
          {initials || <UserCircle2 className="h-4 w-4" />}
        </span>
        <span className="hidden max-w-[120px] truncate text-[15px] font-medium lg:inline">{user.full_name}</span>
        <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground lg:inline" />
      </button>

      {open && (
        <Dropdown onClose={() => setOpen(false)} align="right" label={user.username}>
          <DropdownItem onSelect={() => { setOpen(false); navigate('/hr/me'); }}>
            <UserCircle2 className="mr-2 h-4 w-4 text-muted-foreground" /> My space
          </DropdownItem>
          <DropdownItem onSelect={() => { setOpen(false); void logout(); }}>
            <LogOut className="mr-2 h-4 w-4 text-muted-foreground" /> Sign out
          </DropdownItem>
        </Dropdown>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small dropdown primitives
// ---------------------------------------------------------------------------

function Dropdown({
  children, onClose, align = 'left', label,
}: {
  children: React.ReactNode;
  onClose: () => void;
  align?: 'left' | 'right';
  label?: string;
}) {
  return (
    <>
      {/* Clicking anywhere else closes the menu. */}
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        role="menu"
        className={cn(
          'absolute z-50 mt-1.5 min-w-[220px] max-w-[320px] overflow-hidden rounded-lg border border-border bg-popover py-1 shadow-raised',
          align === 'right' ? 'right-0' : 'left-0',
        )}
      >
        {label && (
          <div className="px-3 pb-1 pt-1.5 text-[13px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </div>
        )}
        {children}
      </div>
    </>
  );
}

function DropdownItem({
  children, onSelect, selected = false,
}: {
  children: React.ReactNode;
  onSelect: () => void;
  selected?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      className={cn(
        'flex w-full items-center px-3 py-1.5 text-left text-[15px] transition-colors',
        selected ? 'bg-accent font-medium text-accent-foreground' : 'hover:bg-accent/60',
      )}
    >
      {children}
    </button>
  );
}
